import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDateTime } from "@/lib/format";
import { toast } from "sonner";
import { Bell, CheckCheck, AlertOctagon, AlertTriangle, Info, Trash2, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";

const LEVEL = {
  kritisk: { icon: AlertOctagon, cls: "status-forfalt" },
  advarsel: { icon: AlertTriangle, cls: "status-forfaller_snart" },
  info: { icon: Info, cls: "status-ma_kontrolleres" },
};

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = () => api.get("/notifications", { params: { limit: 100 } }).then((r) => setData(r.data)).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => { load(); }, []);

  const refreshBell = () => window.dispatchEvent(new Event("rp:refresh-notifications"));

  const markAll = async () => { await api.post("/notifications/read-all"); toast.success("Alle varsler markert som lest"); load(); refreshBell(); };
  const open = async (n) => { if (!n.read) { try { await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ } } refreshBell(); if (n.link) navigate(n.link); else if (n.invoice_id) navigate(`/fakturaer/${n.invoice_id}`); else load(); };
  const remove = async (n) => { await api.delete(`/notifications/${n.id}`); load(); refreshBell(); };

  return (
    <div className="space-y-6">
      <PageHeader title="Varsler" description="Påminnelser om forfall, duplikater og regninger som må kontrolleres." actions={data?.unread ? <Button variant="outline" onClick={markAll} data-testid="notifications-mark-all-read"><CheckCheck className="h-4 w-4 mr-2" /> Merk alle som lest</Button> : null} />
      {!data ? <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div>
        : data.items.length === 0 ? <EmptyState icon={Bell} title="Ingen varsler" description="Du får varsel her når regninger nærmer seg forfall, eller når noe må kontrolleres." testId="notifications-empty-state" />
        : (
          <ul className="space-y-2" data-testid="notifications-list">
            {data.items.map((n) => {
              const lv = LEVEL[n.level] || LEVEL.info;
              const Icon = lv.icon;
              return (
                <li key={n.id} className={cn("rounded-xl border bg-card p-4 flex items-start gap-3", !n.read && "border-l-4 border-l-primary")} data-testid={`notification-${n.id}`}>
                  <div className={cn("h-9 w-9 rounded-lg border flex items-center justify-center shrink-0", lv.cls)}><Icon className="h-4 w-4" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2"><p className={cn("font-medium", !n.read && "text-foreground")}>{n.title}</p>{!n.read ? <span className="h-2 w-2 rounded-full bg-primary" aria-label="Ulest" /> : null}</div>
                    <p className="text-sm text-muted-foreground">{n.body}</p>
                    <p className="text-xs text-muted-foreground mt-1">{formatDateTime(n.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {n.invoice_id || n.link ? <Button variant="outline" size="sm" onClick={() => open(n)} data-testid={`notification-open-${n.id}`}><ExternalLink className="h-4 w-4 mr-1" /> Åpne</Button> : null}
                    <Button variant="ghost" size="icon" onClick={() => remove(n)} aria-label="Slett varsel" data-testid={`notification-delete-${n.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
    </div>
  );
}
